import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { HeartHandshake, Users, Award, Gift, CheckCircle, AlertCircle, TrendingUp } from 'lucide-react';

const STATS = [
  { Icon: Users,  color: 'text-brand-indigo',  bg: 'bg-brand-indigo/10',  border: 'border-brand-indigo/18',  title: 'Soon',  sub: 'Students to Benefit' },
  { Icon: Award,  color: 'text-brand-emerald', bg: 'bg-brand-emerald/10', border: 'border-brand-emerald/18', title: 'Soon',  sub: 'Annual Scholarships'  },
  { Icon: Gift,   color: 'text-brand-amber',   bg: 'bg-brand-amber/10',   border: 'border-brand-amber/18',   title: 'Soon',  sub: 'Yearly Investment'    },
];

const BENEFITS = [
  { color: 'bg-brand-indigo',  dot: 'bg-brand-indigo',  text: 'Merit-based financial assistance'  },
  { color: 'bg-brand-emerald', dot: 'bg-brand-emerald', text: 'Learning resource provision'        },
  { color: 'bg-brand-amber',   dot: 'bg-brand-amber',   text: 'Educational facility enhancement'  },
];

const INITIAL_FORM = { name: '', email: '', amount: '', message: '' };

const ImpactSupportSection = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [success,  setSuccess]  = useState(false);

  const handleChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await fetch('/api/pledges', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name:    formData.name,
          email:   formData.email,
          amount:  parseFloat(formData.amount),
          message: formData.message || null,
        }),
      });

      if (!response.ok) {
        let msg = `Server error: ${response.status}`;
        try { const d = await response.json(); msg = d.error || msg; } catch { /* ignore parse error */ }
        throw new Error(msg);
      }

      const result = await response.json();
      if (!result.success) throw new Error(result.error || 'Failed to submit pledge');

      setSuccess(true);
      setFormData(INITIAL_FORM);
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="impact" className="py-20 md:py-28 mesh-gradient">
      <div className="container mx-auto px-4 sm:px-6">

        {/* Badge + Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
        >
          <div className="flex justify-center mb-4">
            <span className="section-badge">
              <TrendingUp className="h-3 w-3" />
              Make A Difference
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-5">
            Support <span className="gradient-text">Students</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">
            Every contribution creates opportunities for deserving students. Partner with us to
            build a brighter educational future.
          </p>
        </motion.div>

        {/* Stat cards */}
        <div className="grid sm:grid-cols-3 gap-5 mb-14">
          {STATS.map(({ Icon, color, bg, border, title, sub }, i) => (
            <motion.div
              key={sub}
              className={`glassmorphism-card border ${border} rounded-2xl p-7 text-center
                          hover-lift group`}
              initial={{ opacity: 0, scale: 0.92 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.45 }}
            >
              <div className={`inline-flex p-3.5 rounded-2xl ${bg} mb-4
                               group-hover:scale-110 transition-transform duration-300`}>
                <Icon className={`h-7 w-7 ${color}`} />
              </div>
              <p className="text-2xl font-extrabold text-foreground tracking-tight">{title}</p>
              <p className="text-muted-foreground text-sm mt-1.5 font-medium">{sub}</p>
            </motion.div>
          ))}
        </div>

        {/* Pledge card */}
        <motion.div
          className="glassmorphism-card border border-border/50 rounded-3xl shadow-2xl
                     p-8 md:p-12 max-w-4xl mx-auto relative overflow-hidden"
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {/* Top accent bar */}
          <div className="absolute top-0 left-0 right-0 h-1 brand-gradient-bg rounded-t-3xl" />

          <div className="grid md:grid-cols-2 gap-12 items-center">

            {/* Left info */}
            <div>
              <div className="inline-flex p-4 bg-brand-indigo/10 rounded-2xl mb-5 shadow-sm">
                <HeartHandshake className="h-10 w-10 text-brand-indigo" />
              </div>
              <h3 className="text-2xl font-bold mb-3 tracking-tight">Invest in Academic Excellence</h3>
              <p className="text-muted-foreground leading-relaxed mb-7 text-[15px]">
                Your support enables talented students to access quality education and unlock
                their potential for success.
              </p>

              {/* Benefits */}
              <ul className="space-y-3.5">
                {BENEFITS.map(({ dot, text }) => (
                  <li key={text} className="flex items-center gap-3 text-sm text-foreground font-medium">
                    <span className={`w-2 h-2 rounded-full ${dot} flex-shrink-0`} />
                    {text}
                  </li>
                ))}
              </ul>
            </div>

            {/* Pledge form */}
            <form onSubmit={handleSubmit} className="space-y-4">

              {/* Success message */}
              {success && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-950/30
                             border border-green-200 dark:border-green-800 text-green-800
                             dark:text-green-300 rounded-xl text-sm font-medium"
                >
                  <CheckCircle className="h-4 w-4 flex-shrink-0" />
                  Thank you! Your pledge has been received. We'll contact you soon.
                </motion.div>
              )}

              {/* Error message */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-3 p-4 bg-destructive/8 border
                             border-destructive/25 text-destructive rounded-xl text-sm font-medium"
                >
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </motion.div>
              )}

              <Input
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleChange}
                required
                disabled={loading}
              />
              <Input
                name="email"
                type="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={loading}
              />
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground pointer-events-none">
                  ₹
                </span>
                <Input
                  name="amount"
                  type="number"
                  placeholder="Pledge Amount (INR)"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                  min="1"
                  step="0.01"
                  disabled={loading}
                  className="pl-8"
                />
              </div>
              <Textarea
                name="message"
                placeholder="Message (Optional)"
                rows={3}
                value={formData.message}
                onChange={handleChange}
                disabled={loading}
              />

              <Button
                type="submit"
                size="lg"
                className="w-full h-12 text-[15px] font-semibold
                           bg-gradient-to-r from-brand-indigo to-brand-emerald
                           hover:from-brand-indigo/92 hover:to-brand-emerald/92
                           shadow-lg shadow-brand-indigo/25 hover:shadow-xl
                           hover:shadow-brand-indigo/30 transition-all duration-200"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Processing…
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <HeartHandshake className="h-4 w-4" />
                    Commit to Excellence
                  </span>
                )}
              </Button>
            </form>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ImpactSupportSection;
